import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import type { AccountLoginUser } from '@automatick/shared-client-ng';
import {
  RealtimeMediaSignalKind,
  RealtimeRecipientLevel,
  type IceServerEnvironment,
  NotificationHubContract,
} from '@automatick/shared-ts';
import { AuthService, type Session, type Token } from '@seech/auth-ng';
import {
  RealTimeCommunicationService,
  type RealTimeCommunicationEnvelope,
} from '@seech/core-ng';
import {
  RealtimeMediaClientService,
  RealtimeMediaSessionCreateRequest,
  RealtimeMediaSessionMetadata,
  RealtimeMediaSignalPayload,
  RealtimeMediaSignalPublishRequest,
  RealtimeRecipientDescriptor,
  RealtimeVoiceFallbackContract,
  RealtimeVoiceLanguagePreferenceContract,
  RealtimeVoiceParticipantContextContract,
  RealtimeVoiceProviderClientConnectionContract,
  RealtimeVoiceSessionStartRequest,
  RealtimeVoiceSessionStartResponse,
} from '@automatick/shared-ng';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { buildRealtimeStartOptions } from '../shared/realtime/realtime-start-options';
import {
  buildRealtimeConnectionHealth,
  buildIdleRealtimeConnectionHealth,
} from '../shared/realtime/realtime-connection-health';
import { CurrentDeviceContextService } from './current-device-context.service';
import {
  decodeRealtimeMediaSignalNotification,
  projectRealtimeIceCandidatePayload,
  projectRealtimeMediaSessionMetadata,
  projectRealtimeVoiceStartEvidence,
  projectRtcIceCandidate,
  type CanonicalRealtimeMediaSignal,
} from './realtime-media-contract';
import {
  RealtimeMediaPeerRuntime,
  type RealtimeMediaControlMode,
  type RealtimeMediaPeerPlatform,
  type RealtimeMediaPeerRuntimeEvent,
  type RealtimeMediaProviderAttachment,
  type RealtimeMediaSessionView,
} from './realtime-media-peer.runtime';

export type {
  RealtimeMediaControlMode,
  RealtimeMediaSessionView,
} from './realtime-media-peer.runtime';

export const RealtimeSessionTypes = {
  AutomationRemoteControl: 'automation.remote-control',
  CollaborationRemoteControl: 'collaboration.remote-control',
  CollaborationRemoteView: 'collaboration.remote-view',
  ConversationRealtimeVoice: 'conversation.realtime-voice',
  ConversationLiveInterpreter: 'conversation.live-interpreter',
  SupportRemoteControl: 'support.remote-control',
  SupportRemoteView: 'support.remote-view',
} as const;

export const RealtimeRuntimeCapabilities = {
  RealtimeVoice: 'realtime.voice',
  LiveInterpreter: 'realtime.live-interpreter',
} as const;

export const RealtimeMediaControlStates = {
  Approved: 'approved',
  AutoApproved: 'auto-approved',
  ManualDrive: 'manual-drive',
  AutoDrive: 'auto-drive',
  Paused: 'paused',
  Canceled: 'canceled',
  Ended: 'ended',
  Declined: 'declined',
} as const;

export const RealtimeSessionControlStates = {
  ViewOnly: 'view-only',
  ControlRequested: 'control-requested',
  ControlGranted: 'control-granted',
  ActiveControl: 'active-control',
  HandoffRequested: 'handoff-requested',
  ManualDrive: 'manual-drive',
  AutoResumePending: 'auto-resume-pending',
  Paused: 'paused',
  Revoked: 'revoked',
  Denied: 'denied',
  Released: 'released',
  Ended: 'ended',
} as const;

export const RealtimeSessionControlActions = {
  RequestControl: 'request-control',
  ApproveControl: 'approve-control',
  DenyControl: 'deny-control',
  RevokeControl: 'revoke-control',
  ReleaseControl: 'release-control',
  HandoffControl: 'handoff-control',
} as const;

const realtimeRecipientLevels: ReadonlySet<string> = new Set(
  Object.values(RealtimeRecipientLevel),
);
const realtimeSourceKinds: ReadonlySet<string> = new Set([
  'screen',
  'camera',
  'microphone',
  'speaker',
  'video',
  'audio',
]);
const realtimeMediaKinds: ReadonlySet<string> = new Set([
  'audio',
  'video',
  'screen',
  'transcription',
  'translation',
]);

type RealtimeMediaSignalPublishInput = Omit<
  RealtimeMediaSignalPublishRequest,
  'mediaSessionId' | 'payload' | 'metadata'
> & {
  mediaSessionId?: string;
  payload?: RealtimeMediaSignalPayload | null;
  metadata?: RealtimeMediaSessionMetadata | null;
};

function buildIceServers(): RTCIceServer[] {
  const configuredSource: readonly IceServerEnvironment[] =
    environment.WEBRTC_ICE_SERVERS;
  const configured = configuredSource
    .filter((server) => {
      const urls = server.urls;
      return typeof urls === 'string'
        ? urls.trim().length > 0
        : urls.some((url) => url.trim().length > 0);
    })
    .map((server) => {
      const urls = server.urls;
      return {
        urls:
          typeof urls === 'string'
            ? urls.trim()
            : urls.map((url) => url.trim()).filter((url) => url.length > 0),
        username: server.username?.trim() || undefined,
        credential: server.credential?.trim() || undefined,
      };
    });

  return configured;
}

function createBrowserPeerPlatform(): RealtimeMediaPeerPlatform {
  return {
    createPeerConnection: (configuration) =>
      new RTCPeerConnection(configuration),
    createMediaStream: () => new MediaStream(),
    createSessionDescription: (description) =>
      new RTCSessionDescription(description),
    createIceCandidate: (candidate) => new RTCIceCandidate(candidate),
    getDisplayMedia: (constraints) =>
      navigator.mediaDevices.getDisplayMedia(constraints),
    getUserMedia: (constraints) =>
      navigator.mediaDevices.getUserMedia(constraints),
  };
}

@Injectable({ providedIn: 'root' })
export class RealtimeMediaService implements OnDestroy {
  private readonly authService = inject(
    AuthService<AccountLoginUser, Session, Token>,
  );
  private readonly realtimeMediaClient = inject(RealtimeMediaClientService);
  private readonly currentDeviceContext = inject(CurrentDeviceContextService);
  private readonly realtime = inject(RealTimeCommunicationService);
  private readonly zone = inject(NgZone);
  private readonly destroy$ = new Subject<void>();
  private readonly stateSubject = new BehaviorSubject<
    RealtimeMediaSessionView[]
  >([]);
  private readonly connectionHealthSubject = new BehaviorSubject(
    buildIdleRealtimeConnectionHealth(NotificationHubContract.ReceiveMethod),
  );
  private readonly peerRuntime: RealtimeMediaPeerRuntime;
  private isDestroyed = false;

  readonly sessions$ = this.stateSubject.asObservable();
  readonly connectionHealth$ = this.connectionHealthSubject.asObservable();

  constructor() {
    this.peerRuntime = new RealtimeMediaPeerRuntime(
      createBrowserPeerPlatform(),
      { iceServers: buildIceServers() },
      (event) => this.handlePeerRuntimeEvent(event),
    );
    this.realtime.events$.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      void this.handleRealtimeEnvelopeAsync(event).catch(() => undefined);
    });

    this.realtime.status$.pipe(takeUntil(this.destroy$)).subscribe((status) => {
      this.zone.run(() => {
        this.connectionHealthSubject.next(
          buildRealtimeConnectionHealth(status),
        );
        if (this.connectionHealthSubject.value.isConnected) {
          this.emitState();
        }
      });
    });
  }

  ngOnDestroy(): void {
    if (this.isDestroyed) return;

    this.isDestroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.peerRuntime.destroy();
  }

  async startSession(args: {
    title?: string | null;
    recipients: RealtimeRecipientDescriptor[];
    requestedMediaKinds?: string[];
    preferredSourceKinds?: string[];
    metadata?: RealtimeMediaSessionMetadata | null;
  }): Promise<void> {
    if (this.isDestroyed) return;

    const recipients = this.projectExactRecipients(args.recipients);
    if (recipients.length === 0) return;

    const preferredSourceKinds = this.projectExactStrings(
      args.preferredSourceKinds,
      ['camera', 'microphone'],
      realtimeSourceKinds,
    );
    if (preferredSourceKinds.length === 0) return;
    const requestedMediaKinds = this.projectExactStrings(
      args.requestedMediaKinds,
      this.inferMediaKinds(preferredSourceKinds),
      realtimeMediaKinds,
    );
    if (requestedMediaKinds.length === 0) return;

    await this.ensureConnectedAsync();
    if (this.isDestroyed) return;

    const captureLease = await this.peerRuntime
      .capture(preferredSourceKinds)
      .catch(() => null);
    if (!captureLease) {
      if (this.isDestroyed) return;
      throw new Error('Realtime media capture could not be started.');
    }

    const created = await firstValueFrom(
      this.realtimeMediaClient.createSession({
        body: {
          title: args.title ?? undefined,
          preferredTransport: 'webrtc',
          requestedMediaKinds,
          preferredSourceKinds,
          targetRecipients: recipients,
          autoDispatchInvite: false,
          metadata: args.metadata ?? undefined,
        } satisfies RealtimeMediaSessionCreateRequest,
      }),
    ).catch((error: unknown) => {
      this.peerRuntime.releaseCapture(captureLease);
      throw error;
    });

    if (this.isDestroyed) {
      this.peerRuntime.releaseCapture(captureLease);
      return;
    }

    const mediaSessionId = created.mediaSessionId;
    if (!this.isExactNonEmptyString(mediaSessionId)) {
      this.peerRuntime.releaseCapture(captureLease);
      throw new Error('Realtime media session creation failed.');
    }

    const started = await this.peerRuntime.startOutbound(
      {
        mediaSessionId,
        title: args.title?.trim() || null,
        phase: 'calling',
        direction: 'outbound',
        requestedMediaKinds,
        preferredSourceKinds,
        metadata: args.metadata ?? null,
        sessionType: this.readExactString(args.metadata?.sessionType),
        controlMode: this.resolveControlMode(args.metadata),
        targetRecipients: recipients,
        replyRecipients: [],
        localStream: null,
        remoteStream: null,
        providerSession: null,
        providerClientConnection: null,
        fallback: null,
        errorMessage: null,
      },
      captureLease,
      {
        ...(args.metadata ?? {}),
        title: args.title ?? null,
        requestedMediaKinds,
        preferredSourceKinds,
      },
    );
    if (
      !started &&
      this.peerRuntime.getSession(mediaSessionId)?.phase === 'error'
    ) {
      throw new Error('Realtime media session could not be started.');
    }
  }

  async acceptSession(
    mediaSessionId: string,
    preferredSourceKinds?: readonly string[],
  ): Promise<void> {
    const session = this.peerRuntime.getSession(mediaSessionId);
    if (!session) return;
    const sourceKinds = this.projectExactStrings(
      preferredSourceKinds?.length
        ? preferredSourceKinds
        : session.preferredSourceKinds,
      ['camera', 'microphone'],
      realtimeSourceKinds,
    );
    if (sourceKinds.length === 0) return;
    const requestedMediaKinds = this.projectExactStrings(
      session.requestedMediaKinds,
      this.inferMediaKinds(sourceKinds),
      realtimeMediaKinds,
    );
    if (requestedMediaKinds.length === 0) return;
    const accepted = await this.peerRuntime.accept(
      mediaSessionId,
      sourceKinds,
      requestedMediaKinds,
    );
    if (
      !accepted &&
      this.peerRuntime.getSession(mediaSessionId)?.phase === 'error'
    ) {
      throw new Error('Realtime media session could not be connected.');
    }
  }

  declineSession(mediaSessionId: string): void {
    const session = this.peerRuntime.getSession(mediaSessionId);
    if (!session) return;

    void this.publishSignalAsync(mediaSessionId, {
      signalKind: RealtimeMediaSignalKind.Ended,
      targetRecipients: [...session.replyRecipients],
      metadata: {
        ...(session.metadata ?? {}),
        state: RealtimeMediaControlStates.Declined,
      },
    }).catch(() => undefined);
    this.peerRuntime.remove(mediaSessionId);
  }

  endSession(mediaSessionId: string): void {
    const session = this.peerRuntime.getSession(mediaSessionId);
    if (!session) return;

    const recipients = this.projectExactRecipients([
      ...session.targetRecipients,
      ...session.replyRecipients,
    ]);
    void this.publishSignalAsync(mediaSessionId, {
      signalKind: RealtimeMediaSignalKind.Ended,
      targetRecipients: recipients,
      metadata: {
        ...(session.metadata ?? {}),
        state: RealtimeMediaControlStates.Ended,
      },
    }).catch(() => undefined);
    this.peerRuntime.remove(mediaSessionId);
  }

  private async ensureConnectedAsync(): Promise<void> {
    await this.realtime.start(
      buildRealtimeStartOptions({
        currentDeviceContext: this.currentDeviceContext.snapshot(),
        accessTokenFactory: () => this.getAccessTokenAsync(),
      }),
    );
  }

  private async handleRealtimeEnvelopeAsync(
    envelope: RealTimeCommunicationEnvelope<unknown, unknown>,
  ): Promise<void> {
    const signal = decodeRealtimeMediaSignalNotification(envelope.payload);
    if (!signal) return;

    await this.handleNotificationAsync(signal);
  }

  private async handleNotificationAsync(
    signal: CanonicalRealtimeMediaSignal,
  ): Promise<void> {
    const session = this.peerRuntime.getSession(signal.mediaSessionId);
    if (
      !session &&
      (signal.signalKind === RealtimeMediaSignalKind.Invite ||
        signal.signalKind === RealtimeMediaSignalKind.Offer)
    ) {
      if (this.isDestroyed) return;
      const metadata = projectRealtimeMediaSessionMetadata(signal.metadata);
      this.peerRuntime.addInbound(
        {
          mediaSessionId: signal.mediaSessionId,
          title: signal.title,
          phase: 'pending',
          direction: 'inbound',
          requestedMediaKinds: [...signal.requestedMediaKinds],
          preferredSourceKinds: [...signal.preferredSourceKinds],
          metadata,
          sessionType: this.readExactString(metadata?.sessionType),
          controlMode: this.resolveControlMode(metadata),
          targetRecipients: [],
          replyRecipients: signal.replyRecipients.map((recipient) => ({
            ...recipient,
          })),
          localStream: null,
          remoteStream: null,
          providerSession: signal.providerSession,
          providerClientConnection: null,
          fallback: signal.fallback,
          errorMessage: null,
        },
        signal.payload,
      );
      return;
    }

    if (!session) return;

    switch (signal.signalKind) {
      case RealtimeMediaSignalKind.Answer:
        await this.peerRuntime.applyAnswer(
          signal.mediaSessionId,
          signal.payload,
        );
        break;
      case RealtimeMediaSignalKind.Offer:
      case RealtimeMediaSignalKind.Renegotiate:
        await this.peerRuntime.answerOffer(
          signal.mediaSessionId,
          signal.payload,
          signal.replyRecipients,
        );
        break;
      case RealtimeMediaSignalKind.State:
        this.applyStateSignal(signal);
        break;
      case RealtimeMediaSignalKind.Ringing:
        if (session.direction === 'outbound') {
          this.peerRuntime.patch(signal.mediaSessionId, {
            phase: 'connecting',
            errorMessage: null,
          });
        }
        break;
      case RealtimeMediaSignalKind.IceCandidate: {
        const candidate = projectRtcIceCandidate(signal.payload);
        await this.peerRuntime.addIceCandidate(
          signal.mediaSessionId,
          candidate,
        );
        break;
      }
      case RealtimeMediaSignalKind.Ended:
      case RealtimeMediaSignalKind.Busy:
      case RealtimeMediaSignalKind.Declined:
      case RealtimeMediaSignalKind.Missed:
      case RealtimeMediaSignalKind.TimedOut:
      case RealtimeMediaSignalKind.AcceptedElsewhere:
        this.peerRuntime.remove(signal.mediaSessionId);
        break;
    }
  }

  private applyStateSignal(signal: CanonicalRealtimeMediaSignal): void {
    const session = this.peerRuntime.getSession(signal.mediaSessionId);
    if (!session) return;

    const signalMetadata = projectRealtimeMediaSessionMetadata(signal.metadata);
    const state = this.readControlState(signalMetadata);
    if (
      state === RealtimeMediaControlStates.Ended ||
      state === RealtimeMediaControlStates.Declined ||
      state === RealtimeSessionControlStates.Ended ||
      state === RealtimeSessionControlStates.Denied ||
      state === RealtimeSessionControlStates.Released ||
      state === RealtimeSessionControlStates.Revoked
    ) {
      this.peerRuntime.remove(signal.mediaSessionId);
      return;
    }

    const metadata = {
      ...(session.metadata ?? {}),
      ...(signalMetadata ?? {}),
    };
    const providerSession = signal.providerSession ?? session.providerSession;
    const fallback = signal.fallback ?? session.fallback;
    this.peerRuntime.patch(signal.mediaSessionId, {
      metadata,
      sessionType: this.readExactString(metadata.sessionType),
      providerSession,
      fallback,
      requestedMediaKinds: signal.requestedMediaKinds.length
        ? [...signal.requestedMediaKinds]
        : session.requestedMediaKinds,
      preferredSourceKinds: signal.preferredSourceKinds.length
        ? [...signal.preferredSourceKinds]
        : session.preferredSourceKinds,
      controlMode: this.resolveControlMode(metadata),
      errorMessage:
        fallback?.isRealtimeStillActive === false
          ? this.buildVoiceFallbackMessage(fallback)
          : session.errorMessage,
      phase:
        fallback?.isRealtimeStillActive === false
          ? 'error'
          : state === RealtimeMediaControlStates.Approved ||
              state === RealtimeMediaControlStates.AutoApproved
            ? 'connecting'
            : session.phase,
    });
  }

  async startVoiceInterpreterSession(args: {
    title?: string | null;
    runtimeCapability: string;
    latencyPosture?: string | null;
    recipients?: RealtimeRecipientDescriptor[];
    requestedMediaKinds?: string[];
    preferredSourceKinds?: string[];
    metadata: RealtimeMediaSessionMetadata;
    participants?: RealtimeVoiceParticipantContextContract[];
    languagePreferences?: RealtimeVoiceLanguagePreferenceContract[];
  }): Promise<RealtimeVoiceSessionStartResponse> {
    const conversationSessionId = this.readExactString(args.metadata.sessionId);
    if (!conversationSessionId) {
      throw new Error(
        'Conversation session is required to start realtime voice.',
      );
    }

    const runtimeCapability = this.resolveVoiceRuntimeCapability(
      args.runtimeCapability,
    );
    if (!runtimeCapability) {
      throw new Error('Realtime voice configuration is invalid.');
    }
    const isInterpreter =
      runtimeCapability === RealtimeRuntimeCapabilities.LiveInterpreter;
    const sessionType = isInterpreter
      ? RealtimeSessionTypes.ConversationLiveInterpreter
      : RealtimeSessionTypes.ConversationRealtimeVoice;
    const sessionIntent = isInterpreter
      ? RealtimeSessionTypes.ConversationLiveInterpreter
      : RealtimeSessionTypes.ConversationRealtimeVoice;
    const preferredSourceKinds = this.projectExactStrings(
      args.preferredSourceKinds,
      ['microphone'],
      realtimeSourceKinds,
    );
    const requestedMediaKinds = this.projectExactStrings(
      args.requestedMediaKinds,
      isInterpreter
        ? ['audio', 'transcription', 'translation']
        : ['audio', 'transcription'],
      realtimeMediaKinds,
    );
    if (preferredSourceKinds.length === 0 || requestedMediaKinds.length === 0) {
      throw new Error('Realtime voice configuration is invalid.');
    }
    const recipients = this.projectExactRecipients(args.recipients ?? []);
    const title =
      args.title?.trim() ||
      args.metadata.title ||
      (isInterpreter ? 'Live interpreter' : 'Realtime voice');
    const metadata: RealtimeMediaSessionMetadata = {
      ...args.metadata,
      title,
      sessionType,
      sessionIntent,
      requestedMediaKinds,
      preferredSourceKinds,
    };

    await this.ensureConnectedAsync();
    if (this.isDestroyed) {
      throw new Error('Realtime voice is unavailable.');
    }

    const response = await firstValueFrom(
      this.realtimeMediaClient.startVoiceInterpreterSession({
        body: {
          title,
          conversationSessionId,
          runtimeCapability,
          latencyPosture: args.latencyPosture ?? undefined,
          participants: args.participants ?? [],
          languagePreferences: args.languagePreferences ?? [],
          targetRecipients: recipients,
          requestedMediaKinds,
          preferredSourceKinds,
          autoDispatchInvite: true,
        } satisfies RealtimeVoiceSessionStartRequest,
      }),
    );

    if (this.isDestroyed) return response;

    const mediaSession = response.mediaSession ?? null;
    const mediaSessionId = mediaSession?.mediaSessionId;
    if (!this.isExactNonEmptyString(mediaSessionId)) {
      throw new Error('Realtime voice session creation failed.');
    }

    const evidence = projectRealtimeVoiceStartEvidence(response);
    const providerSession = evidence.providerSession;
    const fallback = evidence.fallback;
    const voiceMetadata: RealtimeMediaSessionMetadata = {
      ...metadata,
      realtimeVoiceProvider: providerSession ?? undefined,
      realtimeVoiceFallback: fallback ?? undefined,
    };
    const providerClientConnection = response.providerClientConnection ?? null;
    const responseRequestedMediaKinds = this.projectExactStrings(
      mediaSession?.requestedMediaKinds ?? requestedMediaKinds,
      requestedMediaKinds,
      realtimeMediaKinds,
    );
    const responsePreferredSourceKinds = this.projectExactStrings(
      mediaSession?.preferredSourceKinds ?? preferredSourceKinds,
      preferredSourceKinds,
      realtimeSourceKinds,
    );
    if (
      responseRequestedMediaKinds.length === 0 ||
      responsePreferredSourceKinds.length === 0
    ) {
      throw new Error('Realtime voice session creation failed.');
    }
    await this.peerRuntime.startProviderSession(
      {
        mediaSessionId,
        title,
        phase:
          fallback?.isRealtimeStillActive === false ? 'error' : 'connecting',
        direction: 'outbound',
        requestedMediaKinds: responseRequestedMediaKinds,
        preferredSourceKinds: responsePreferredSourceKinds,
        metadata: voiceMetadata,
        sessionType,
        controlMode: this.resolveControlMode(metadata),
        targetRecipients: recipients,
        replyRecipients: [],
        localStream: null,
        remoteStream: null,
        providerSession,
        providerClientConnection,
        fallback,
        errorMessage:
          fallback?.isRealtimeStillActive === false
            ? this.buildVoiceFallbackMessage(fallback)
            : null,
      },
      this.createProviderAttachment(
        providerClientConnection,
        preferredSourceKinds,
      ),
    );
    return response;
  }

  private createProviderAttachment(
    connectionInfo: RealtimeVoiceProviderClientConnectionContract | null,
    sourceKinds: readonly string[],
  ): RealtimeMediaProviderAttachment | null {
    const endpoint = connectionInfo?.connectionEndpoint;
    const credential = connectionInfo?.ephemeralCredential;
    if (
      !connectionInfo?.isAvailable ||
      typeof endpoint !== 'string' ||
      endpoint.length === 0 ||
      endpoint !== endpoint.trim() ||
      typeof credential !== 'string' ||
      credential.length === 0 ||
      credential !== credential.trim() ||
      connectionInfo.transportKind !== 'WebRtc' ||
      connectionInfo.connectionMode !== 'Client'
    ) {
      return null;
    }
    return {
      sourceKinds,
      exchangeSdp: (offerSdp) =>
        this.exchangeProviderSdp(endpoint, credential, offerSdp),
    };
  }

  private async exchangeProviderSdp(
    endpoint: string,
    credential: string,
    offerSdp: string,
  ): Promise<string> {
    const answer = await fetch(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${credential}`,
        'Content-Type': 'application/sdp',
      },
      body: offerSdp,
    });
    if (!answer.ok) throw new Error('Provider WebRTC SDP exchange failed.');
    return answer.text();
  }

  private async publishSignalAsync(
    mediaSessionId: string,
    request: RealtimeMediaSignalPublishInput,
  ): Promise<void> {
    if (!request.targetRecipients || request.targetRecipients.length === 0)
      return;

    const body: RealtimeMediaSignalPublishRequest = {
      ...request,
      mediaSessionId,
      payload: request.payload ?? undefined,
      metadata: request.metadata ?? undefined,
    };

    await firstValueFrom(
      this.realtimeMediaClient.publishSignal({
        mediaSessionId,
        body,
      }),
    );
  }

  private async handlePeerRuntimeEvent(
    event: RealtimeMediaPeerRuntimeEvent,
  ): Promise<void> {
    if (this.isDestroyed) return;
    if (event.kind === 'state-changed') {
      this.zone.run(() => this.emitState());
      return;
    }
    await this.publishSignalAsync(event.mediaSessionId, {
      signalKind:
        event.kind === 'ice-candidate'
          ? RealtimeMediaSignalKind.IceCandidate
          : event.signalKind,
      targetRecipients: [...event.targetRecipients],
      payload:
        event.kind === 'ice-candidate'
          ? projectRealtimeIceCandidatePayload(event.candidate)
          : event.payload,
      metadata: event.metadata,
    });
  }

  private emitState(): void {
    this.stateSubject.next([...this.peerRuntime.snapshot]);
  }

  private projectExactRecipients(
    recipients: readonly RealtimeRecipientDescriptor[],
  ): RealtimeRecipientDescriptor[] {
    const seen = new Set<string>();
    const projected: RealtimeRecipientDescriptor[] = [];
    for (const recipient of recipients) {
      const level = recipient.level;
      const key = recipient.key ?? null;
      if (
        !realtimeRecipientLevels.has(level) ||
        (key !== null && !this.isExactNonEmptyString(key))
      )
        return [];

      const identity = `${level}:${key ?? ''}`;
      if (!seen.has(identity)) {
        seen.add(identity);
        projected.push({ level, key });
      }
    }
    return projected;
  }

  private inferMediaKinds(sourceKinds: readonly string[]): string[] {
    const sourceSet = new Set(sourceKinds);
    const projected: string[] = [];
    if (['screen', 'camera', 'video'].some((kind) => sourceSet.has(kind)))
      projected.push('video');
    if (['microphone', 'speaker', 'audio'].some((kind) => sourceSet.has(kind)))
      projected.push('audio');
    if (sourceSet.has('screen')) projected.push('screen');
    return projected;
  }

  private projectExactStrings(
    values: readonly string[] | undefined,
    fallback: readonly string[],
    allowedValues: ReadonlySet<string>,
  ): string[] {
    const source = values?.length ? values : fallback;
    return source.every((value) => allowedValues.has(value))
      ? [...new Set(source)]
      : [];
  }

  private readExactString(value: string | null | undefined): string | null {
    return this.isExactNonEmptyString(value) ? value : null;
  }

  private isExactNonEmptyString(
    value: string | null | undefined,
  ): value is string {
    return (
      typeof value === 'string' && value.length > 0 && value === value.trim()
    );
  }

  private buildVoiceFallbackMessage(
    fallback: RealtimeVoiceFallbackContract,
  ): string {
    return (
      this.readExactString(fallback.reasonCode) ??
      this.readExactString(fallback.fallbackKind) ??
      'Realtime voice is unavailable.'
    );
  }

  private resolveVoiceRuntimeCapability(
    runtimeCapability: string | null | undefined,
  ): string | null {
    return runtimeCapability === RealtimeRuntimeCapabilities.LiveInterpreter ||
      runtimeCapability === RealtimeRuntimeCapabilities.RealtimeVoice
      ? runtimeCapability
      : null;
  }

  private resolveControlMode(
    metadata: RealtimeMediaSessionMetadata | null | undefined,
  ): RealtimeMediaControlMode {
    const state = this.readControlState(metadata);
    switch (state) {
      case RealtimeSessionControlStates.ControlRequested:
      case RealtimeSessionControlStates.HandoffRequested:
      case RealtimeSessionControlStates.AutoResumePending:
        return 'requested';
      case RealtimeMediaControlStates.ManualDrive:
      case RealtimeSessionControlStates.ManualDrive:
      case RealtimeSessionControlStates.ActiveControl:
      case RealtimeSessionControlStates.ControlGranted:
        return 'manual';
      case RealtimeMediaControlStates.Paused:
      case RealtimeSessionControlStates.Paused:
        return 'paused';
      case RealtimeMediaControlStates.Canceled:
      case RealtimeSessionControlStates.Ended:
      case RealtimeSessionControlStates.Denied:
      case RealtimeSessionControlStates.Revoked:
      case RealtimeSessionControlStates.Released:
        return 'canceled';
      case RealtimeMediaControlStates.AutoDrive:
      case RealtimeMediaControlStates.Approved:
      case RealtimeMediaControlStates.AutoApproved:
        return 'auto';
      default:
        return this.isRemoteControlMetadata(metadata) ? 'auto' : 'none';
    }
  }

  private isRemoteControlMetadata(
    metadata: RealtimeMediaSessionMetadata | null | undefined,
  ): boolean {
    const sessionType = this.readExactString(metadata?.sessionType);
    return (
      sessionType === RealtimeSessionTypes.SupportRemoteControl ||
      sessionType === RealtimeSessionTypes.CollaborationRemoteControl ||
      sessionType === RealtimeSessionTypes.AutomationRemoteControl
    );
  }

  private readControlState(
    metadata: RealtimeMediaSessionMetadata | null | undefined,
  ): string | null {
    return (
      this.readExactString(metadata?.controlState?.state) ??
      this.readExactString(metadata?.state)
    );
  }

  private async getAccessTokenAsync(): Promise<string> {
    const currentToken = this.authService.getCurrentToken();
    if (currentToken?.access_token && this.authService.isLoggedInNotExpired())
      return currentToken.access_token;

    const refreshToken = currentToken?.refresh_token;
    if (!refreshToken) return currentToken?.access_token ?? '';

    try {
      const refreshedToken = await firstValueFrom(
        this.authService.newAccessToken(refreshToken),
      );
      return refreshedToken?.access_token ?? currentToken?.access_token ?? '';
    } catch {
      return currentToken?.access_token ?? '';
    }
  }
}
